import { useQuery } from '@tanstack/react-query';
import { Play, RotateCcw } from 'lucide-react';
import { AccordionPanel, Button, SectionLabel } from '@wundo/ui';
import { debriefTookPart } from '@wundo/shared/debriefTookPart.ts';
import type { RoleplayCard } from '@wundo/shared/roleplay.ts';
import { fetchRoleplayAttempts } from '@/lib/roleplays.ts';
import { m } from '@/paraglide/messages.js';

export interface PracticeCardPanelProps {
  card: RoleplayCard;
  starting: boolean;
  expanded: boolean;
  onStart: () => void;
  onOpenFeedback: () => void;
}

/**
 * What a practice card shows once it is opened: the situation, what you are
 * practising, what Wundo said last time, and the button to start.
 */
export function PracticeCardPanel({
  card,
  starting,
  expanded,
  onStart,
  onOpenFeedback,
}: PracticeCardPanelProps) {
  const practised = card.attempt_count > 0;

  return (
    <AccordionPanel>
      <div className='flex flex-col gap-5 px-5.5 pb-5.5'>
        <div>
          <SectionLabel>{m.practice_situation()}</SectionLabel>
          <p className='mt-3 text-md text-ink-body'>{card.situation}</p>
        </div>

        <div>
          <SectionLabel>{m.practice_objectives()}</SectionLabel>
          <ul className='mt-3 flex flex-col gap-2.25'>
            {card.learning_objectives.map((objective) => (
              <li key={objective} className='flex items-start gap-2.25'>
                <span
                  aria-hidden='true'
                  className='mt-2 size-1.5 shrink-0 rounded-full bg-accent'
                />
                <span className='text-md text-ink-body'>{objective}</span>
              </li>
            ))}
          </ul>
        </div>

        <PracticeLastTime
          card={card}
          expanded={expanded}
          onOpenFeedback={onOpenFeedback}
        />

        {/*
          No duration line, which the design shows: the API does not know one,
          and a made-up number on a practice card is worse than one line less.
        */}
        <div className='mt-1 flex justify-end'>
          <Button disabled={starting} onClick={onStart}>
            {practised ? (
              <RotateCcw className='size-3.5' aria-hidden='true' />
            ) : (
              <Play className='size-3.5' aria-hidden='true' />
            )}
            {practised ? m.practice_start_again() : m.practice_start()}
          </Button>
        </div>
      </div>
    </AccordionPanel>
  );
}

/**
 * What Wundo said the last time this conversation was practised, in two lines.
 *
 * The recap runs to a paragraph, so it is clamped and ends in the way to read
 * the rest. The query is the feedback dialog's own, key and all, so opening the
 * dialog from here reads what this block already fetched.
 */
function PracticeLastTime({
  card,
  expanded,
  onOpenFeedback,
}: {
  card: RoleplayCard;
  expanded: boolean;
  onOpenFeedback: () => void;
}) {
  const { data: attempts } = useQuery({
    queryKey: ['roleplayAttempts', card.id],
    queryFn: () => fetchRoleplayAttempts(card.id),
    enabled: expanded && card.attempt_count > 0,
  });

  /* Attempts without a debrief were never finished and have nothing to say. */
  const debrief = attempts?.find((attempt) => attempt.debrief !== null)?.debrief;
  if (!debrief) return null;

  /* Nothing was rated, so there is nothing more to read than this. */
  if (!debriefTookPart(debrief)) {
    return (
      <div>
        <SectionLabel>{m.practice_last_time()}</SectionLabel>
        <p className='mt-3 line-clamp-2 text-md text-ink-body'>
          {`${m.practice_not_taken_part_title()}. ${m.practice_not_taken_part_body()}`}
        </p>
      </div>
    );
  }

  return (
    <div>
      <SectionLabel>{m.practice_last_time()}</SectionLabel>
      <p className='mt-3 line-clamp-2 text-md text-ink-body'>{debrief.recap}</p>
      <Button
        variant='link'
        size='sm'
        className='mt-1.5 text-base'
        onClick={onOpenFeedback}
      >
        {m.practice_read_more()}
      </Button>
    </div>
  );
}
